import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../../../config/queryKeys'
import {
  createGame,
  deleteGame,
  saveGame,
  type GameMutationResponse,
} from '../../../../services/game/gameService'
import { buildGamePayload } from '../helpers/buildGamePayload'
import type { GameFormValues } from '../types'

/**
 * Marks the game grid stale after a write.
 *
 * `scheduleGames` is deliberately NOT nested under `game.all` (see the comment
 * at `config/queryKeys.js`): react-query matches by key PREFIX, so nesting them
 * would make every existing `invalidateQueries(game.all)` blow away the whole
 * grid. One call therefore cannot cover both roots -- each is invalidated
 * explicitly.
 *
 * `refetchType: 'none'` marks them stale without firing a request, mirroring
 * `features/game/gameListView/hooks/useGameBulkEdit.ts`. Every list variant
 * (List, Calendar, Details, Team, and each filter combination) holds its own
 * cache under its own key; invalidating the whole root is what stops an
 * inactive-but-fresh variant from serving pre-edit rows the moment the user
 * switches view.
 */
const invalidateGameLists = (
  queryClient: QueryClient,
  { affectsWhichDaysHaveGames }: { affectsWhichDaysHaveGames: boolean }
) => {
  queryClient.invalidateQueries({
    queryKey: queryKeys.scheduleGames.all,
    refetchType: 'none',
  })

  /*
   * The calendar's day index decides which month to OPEN on, not what that
   * month contains, so a stale index is harmless after an in-place edit. Only
   * creating or deleting a game changes which days have games at all.
   */
  if (affectsWhichDaysHaveGames) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.scheduleGameDates.all,
      refetchType: 'none',
    })
  }
}

const invalidateGame = (queryClient: QueryClient, gameId: string) => {
  queryClient.invalidateQueries({
    queryKey: [...queryKeys.game.all, gameId],
    refetchType: 'none',
  })
}

/**
 * These writes are not idempotent, so they must never be replayed.
 *
 * The query client sets `mutations: { retry: 1 }` globally (two attempts), and
 * `req` independently defaults to three transport attempts. Left alone, one
 * click on Confirm could send up to six POSTs and create duplicate games. This
 * pins the React Query half to a single attempt; `gameService` pins the
 * transport half with `maxAttempts: 1`.
 *
 * A failed save surfaces in the modal for the user to retry deliberately.
 */
const NO_RETRY = { retry: 0 } as const

interface CreateGameArgs {
  scheduleId: string
  values: GameFormValues
}

/**
 * POST /schedules/:id/games
 *
 * Resolves the full `{ data }` envelope: the existing success banner reads
 * `data.league_game_number` off it.
 */
export const useCreateGame = () => {
  const queryClient = useQueryClient()

  return useMutation<GameMutationResponse, unknown, CreateGameArgs>({
    ...NO_RETRY,
    mutationFn: ({ scheduleId, values }) =>
      createGame(scheduleId, buildGamePayload(values, { mode: 'create' })),
    onSuccess: () => {
      invalidateGameLists(queryClient, { affectsWhichDaysHaveGames: true })
    },
  })
}

interface UpdateGameArgs {
  gameId: string
  values: GameFormValues
  /** Bracket slot ids, for the submit-time slot guard. */
  homeTeamSlotId?: string | null
  visitingTeamSlotId?: string | null
}

/**
 * PUT /games/:id
 *
 * NOTE: this does not replace the redux saga -- mobile still dispatches
 * `gameActions.update.request`, so the saga, actions and reducer cases all
 * remain. On web the Game page keeps its `readGame(game.id)` refetch, whose
 * `read.success` overwrites the redux entry outright, which is what keeps the
 * Game page and its subpages correct without the saga's merge.
 */
export const useUpdateGame = () => {
  const queryClient = useQueryClient()

  return useMutation<GameMutationResponse, unknown, UpdateGameArgs>({
    ...NO_RETRY,
    mutationFn: ({ gameId, values, homeTeamSlotId, visitingTeamSlotId }) =>
      saveGame(
        gameId,
        buildGamePayload(values, {
          mode: 'edit',
          homeTeamSlotId,
          visitingTeamSlotId,
        })
      ),
    onSuccess: (_data, { gameId }) => {
      invalidateGameLists(queryClient, { affectsWhichDaysHaveGames: false })
      invalidateGame(queryClient, gameId)
    },
  })
}

/** DELETE /games/:id */
export const useDeleteGame = () => {
  const queryClient = useQueryClient()

  return useMutation<void, unknown, { gameId: string }>({
    ...NO_RETRY,
    mutationFn: ({ gameId }) => deleteGame(gameId),
    onSuccess: (_data, { gameId }) => {
      invalidateGameLists(queryClient, { affectsWhichDaysHaveGames: true })
      // Removed rather than invalidated: the game no longer exists, so a
      // refetch would only 404.
      queryClient.removeQueries({ queryKey: [...queryKeys.game.all, gameId] })
    },
  })
}
